import { createIsomorphicFn } from "@tanstack/react-start";
import Axios, {
	type AxiosError,
	AxiosHeaders,
	type AxiosRequestConfig,
	type AxiosResponse as NativeAxiosResponse,
} from "axios";

import { getUmbracoCmsBaseUrlEnv } from "#/_packages/umbrella/config/utils.ts";
import { reportError } from "#/_packages/umbrella/features/error-handling/report-error";
import { clearPreviewCookie } from "#/_packages/umbrella/features/previews/utils/cookie-utils.ts";
import {
	getRequestPreviewMode,
	getRequestPreviewToken,
	isPreviewSearchEnabled,
} from "#/_packages/umbrella/features/previews/utils/request-preview-mode.ts";
import { recordRequestDiagnostic } from "./request-error.ts";

export const PREVIEW_TOKEN_HEADER = "X-Umbrella-Preview-Token";

const getServerRequestCookieHeader = createIsomorphicFn()
	.server(async () => {
		const { getRequestHeader } = await import("@tanstack/react-start/server");
		return getRequestHeader("cookie");
	})
	.client(() => undefined);

export const shouldIncludePreviewHeader = (
	requestHeaders: Record<string, string>,
	previewValue: string | null | undefined,
): boolean => {
	if (requestHeaders.Preview) return false;
	return isPreviewSearchEnabled(previewValue);
};

const url = getUmbracoCmsBaseUrlEnv();

export const AXIOS_INSTANCE = Axios.create({
	baseURL: url,
	withCredentials: true,
});

type AxiosResponse<T> = {
	data: T;
	status: number;
	headers: NativeAxiosResponse["headers"];
};

export function toAxiosHeaders(values: AxiosRequestConfig["headers"]) {
	const headers = new AxiosHeaders();
	for (const [name, value] of Object.entries(values ?? {})) {
		if (value !== undefined) headers.set(name, value);
	}
	return headers;
}

const getHeaders = async (
	config?: AxiosRequestConfig,
): Promise<AxiosHeaders> => {
	const headers = toAxiosHeaders(config?.headers);

	const cookieHeader = await getServerRequestCookieHeader();

	if (cookieHeader && !headers.has("Cookie")) {
		headers.set("Cookie", cookieHeader);
	}
	const previewRequested = headers.has("Preview")
		? isPreviewSearchEnabled(headers.get("Preview"))
		: await getRequestPreviewMode();
	headers.delete("Preview");
	headers.set("Preview", previewRequested ? "true" : "false");
	const target = new URL(
		config?.url ?? "/",
		config?.baseURL ?? url ?? "https://request.invalid",
	);
	const sameCms = url !== undefined && target.origin === new URL(url).origin;
	if (
		previewRequested &&
		sameCms &&
		target.pathname.startsWith("/umbraco/delivery/api/")
	) {
		const token =
			headers.get(PREVIEW_TOKEN_HEADER) ?? (await getRequestPreviewToken());
		if (
			typeof token === "string" &&
			token.length <= 4096 &&
			/^[A-Za-z0-9_-]+$/.test(token)
		)
			headers.set(PREVIEW_TOKEN_HEADER, token);
		else headers.delete(PREVIEW_TOKEN_HEADER);
	} else {
		headers.delete(PREVIEW_TOKEN_HEADER);
	}

	return headers;
};

export const customInstance = async <T>(
	config: AxiosRequestConfig,
	options?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
	const mergedConfig: AxiosRequestConfig = {
		...config,
		...options,
		headers: await getHeaders({
			...config,
			...options,
			headers: toAxiosHeaders(config.headers).concat(
				toAxiosHeaders(options?.headers),
			),
		}),
		paramsSerializer: { indexes: null },
		withCredentials: true,
	};

	let response: NativeAxiosResponse<T>;
	try {
		response = await AXIOS_INSTANCE.request<T>(mergedConfig);
	} catch (error) {
		if (Axios.isAxiosError(error)) {
			const headers = toAxiosHeaders(mergedConfig.headers);
			if (headers.has(PREVIEW_TOKEN_HEADER)) {
				if (error.config) {
					error.config.headers = AxiosHeaders.from(error.config.headers);
					error.config.headers.delete(PREVIEW_TOKEN_HEADER);
				}
				if (error.response?.config) {
					error.response.config.headers = AxiosHeaders.from(
						error.response.config.headers,
					);
					error.response.config.headers.delete(PREVIEW_TOKEN_HEADER);
				}
				if (error.response) error.response.request = undefined;
				error.request = undefined;
			}
		}
		if (Axios.isAxiosError(error) && !Axios.isCancel(error)) {
			if (!error.response || error.response.status >= 500)
				reportError(error, {
					operation: "cms-request",
					status: error.response?.status,
				});
			const headers = toAxiosHeaders(mergedConfig.headers);
			const method = (mergedConfig.method ?? "GET").toUpperCase();
			const endpoint = new URL(
				mergedConfig.url ?? "/",
				"https://request.invalid",
			).pathname;
			const status = error.response?.status;
			const rejectedPreview =
				status === 401 &&
				headers.get("Preview") === "true" &&
				endpoint.startsWith("/umbraco/delivery/api/");
			if (rejectedPreview) await clearPreviewCookie();
			const responseData: unknown = error.response?.data;
			const traceId =
				error.response?.headers["x-request-id"] ??
				error.response?.headers["x-correlation-id"] ??
				(typeof responseData === "object" &&
				responseData !== null &&
				"traceId" in responseData
					? responseData.traceId
					: undefined);
			const requestId =
				typeof traceId === "string" && /^[a-zA-Z0-9:._-]{1,200}$/.test(traceId)
					? `; request ID: ${traceId}`
					: "";
			const configurationDetail =
				status === 500 &&
				/^\/api\/v\d+\/commerce\/product(?:\/|$)/.test(endpoint) &&
				typeof responseData === "object" &&
				responseData !== null &&
				"code" in responseData &&
				responseData.code === "commerce_store_missing"
					? " Select an existing Commerce store on the site's Start page, then publish it."
					: status === 400 &&
							endpoint === "/umbraco/delivery/api/v2/site-settings" &&
							typeof responseData === "string" &&
							responseData.startsWith(
								"Domain not found matching normalizedDomain: ",
							)
						? " Register the API host as a CMS domain on the site's Start page."
						: "";
			const previewDetail = rejectedPreview
				? " Open a new preview from the backoffice; the editor token may be missing, expired, or invalid."
				: "";
			recordRequestDiagnostic(
				error,
				`${method} ${endpoint} failed (${status ? `HTTP ${status}` : "no HTTP response"}${requestId}). Check the Backoffice logs for this request.${configurationDetail}${previewDetail}`,
			);
		}
		throw error;
	}

	return {
		data: response.data,
		status: response.status,
		headers: response.headers,
	};
};

export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;
